import React, { useState, useEffect } from 'react';
import { Newspaper, ExternalLink, RefreshCw, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoldNewsSection = () => {
  const { t, currentLang } = useLanguage();
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API}/news`);
        if (!response.ok) throw new Error('Failed to fetch news');
        const data = await response.json();
        setNews(data.news || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Unable to load news');
        setNews([
          {
            title: "Gold Prices Continue Upward Trend",
            summary: "Gold maintains its position as a safe-haven asset amid global economic uncertainty.",
            url: "https://www.gold.org/goldhub",
            source: "World Gold Council",
            date: new Date().toISOString().split('T')[0]
          },
          {
            title: "Central Banks Increase Gold Reserves",
            summary: "Central banks worldwide continue to add gold to their reserves, supporting long-term demand.",
            url: "https://www.gold.org/goldhub/data/gold-reserves-by-country",
            source: "World Gold Council",
            date: new Date().toISOString().split('T')[0]
          },
          {
            title: "Gold Investment Outlook Positive",
            summary: "Analysts remain bullish on gold as inflation hedging and portfolio diversification drive demand.",
            url: "https://www.lbma.org.uk",
            source: "LBMA",
            date: new Date().toISOString().split('T')[0]
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const locale = currentLang === 'tr' ? 'tr-TR' : currentLang === 'el' ? 'el-GR' : currentLang === 'ar' ? 'ar-SA' : 'en-US';
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <section id="news" className="py-20 md:py-28 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('goldMarketNews')}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('latestNews')}
          </p>
        </div>

        {/* News Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37]" />
            <span className="ml-2 text-gray-400">{t('loading')}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.slice(0, 6).map((item, index) => (
              <Card 
                key={index} 
                className="card-hover bg-[#12121a] border-[#1e1e2e] hover:border-[#D4AF37]/30 rounded-2xl overflow-hidden group"
              >
                <CardContent className="p-6">
                  {/* Source & Date */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-[#D4AF37] uppercase tracking-wide">
                      {item.source}
                    </span>
                    {item.date && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(item.date)}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                    {item.title}
                  </h3>

                  {/* Summary */}
                  <p className="text-sm text-gray-400 mb-4 line-clamp-3">
                    {item.summary}
                  </p>

                  {/* Read More Link */}
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-[#D4AF37] hover:text-[#F5ECD7] transition-colors"
                    >
                      {t('readMore')}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* More News Links */}
        <div className="mt-10 text-center">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { name: 'World Gold Council', url: 'https://www.gold.org/goldhub' },
              { name: 'LBMA', url: 'https://www.lbma.org.uk/prices-and-data' },
              { name: 'Gold Price News', url: 'https://goldprice.org/gold-news' }
            ].map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#12121a] hover:bg-[#1e1e2e] border border-[#1e1e2e] hover:border-[#D4AF37]/30 text-gray-300 hover:text-[#D4AF37] rounded-xl text-sm font-medium transition-all"
              >
                <Newspaper className="w-4 h-4" />
                {link.name}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoldNewsSection;
